import * as t from 'typegate';
import { Language } from '../localization';

export enum ComprehensivePricingSupportedPaymentModes {
  annual = 'annual',
  quarterly = 'quarterly',
  monthly = 'monthly',
  semiAnnual = 'semiAnnual',
  singlePremium = 'singlePremium',
}

export enum ComprehensivePricingCurrency {
  USD = 'usd',
  CAD = 'cad',
}

const comprehensivePricingCostSchema = t.object(
  t.optionalProperty(ComprehensivePricingCurrency.USD, t.number),
  t.optionalProperty(ComprehensivePricingCurrency.CAD, t.number),
);

export type ComprehensivePricingCost = t.Infer<typeof comprehensivePricingCostSchema>;

const comprehensivePricingCostsSchema = t.object(
  t.optionalProperty('monthlyCost', comprehensivePricingCostSchema),
  t.optionalProperty('quarterlyCost', comprehensivePricingCostSchema),
  t.optionalProperty('annualCost', comprehensivePricingCostSchema),
  t.optionalProperty('semiAnnualCost', comprehensivePricingCostSchema),
  t.optionalProperty('singlePremiumCost', comprehensivePricingCostSchema),
);

export type ComprehensivePricingCosts = t.Infer<typeof comprehensivePricingCostsSchema>;

export const comprehensivePricingRiderSchema = t.intersection(
  comprehensivePricingCostsSchema,
  t.object(
    t.optionalProperty('coverageAmount', t.number),
    t.property(
      'displayName',
      t.object(t.optionalProperty(Language.fr, t.string), t.optionalProperty(Language.en, t.string)),
    ),
    t.property('productCode', t.string),
  ),
);
export type ComprehensivePricingRider = t.Infer<typeof comprehensivePricingRiderSchema>;

const comprehensivePricingDisplayNameSchema = t.object(
  t.optionalProperty(Language.fr, t.string),
  t.optionalProperty(Language.en, t.string),
);

export type ComprehensivePricingDisplayName = t.Infer<typeof comprehensivePricingDisplayNameSchema>;

export const comprehensivePricingProductSchema = t.intersection(
  comprehensivePricingCostsSchema,
  t.object(
    t.optionalProperty('coverageAmount', t.number),
    t.property('displayName', comprehensivePricingDisplayNameSchema),
    t.property('productCode', t.string),
    t.property('riders', t.array(comprehensivePricingRiderSchema)),
    t.property('isJointProduct', t.boolean),
    t.property('isDuplicateCost', t.boolean),
  ),
);

export type ComprehensivePricingProduct = t.Infer<typeof comprehensivePricingProductSchema>;

export enum ComprehensivePricingPartyRoleCode {
  insured = 'insured',
  owner = 'owner',
}

export const comprehensivePricingPartyRoleSchema = t.object(
  t.property('roleCode', t.tsEnum(ComprehensivePricingPartyRoleCode)),
  t.optionalProperty('roleIndex', t.number),
);

export type ComprehensivePricingPartyRole = t.Infer<typeof comprehensivePricingPartyRoleSchema>;

export const comprehensivePricingPartySchema = t.object(
  t.property('firstName', t.string),
  t.property('lastName', t.string),
  t.property('roles', t.array(comprehensivePricingPartyRoleSchema)),
  t.property('riders', t.array(comprehensivePricingRiderSchema)),
  t.property('products', t.array(comprehensivePricingProductSchema)),
);

export type ComprehensivePricingParty = t.Infer<typeof comprehensivePricingPartySchema>;

const feesInformationSchema = t.object(
  t.optionalProperty('informationMessage', t.string),
  t.optionalProperty('hideFees', t.boolean),
);

export const comprehensivePricingContractSchema = t.intersection(
  comprehensivePricingCostsSchema,
  t.object(
    t.optionalProperty('equivalentSingleAge', t.number),
    t.optionalProperty('equivalentSmokerStatus', t.union(t.literal('smoker'), t.literal('nonSmoker'))),
    t.optionalProperty('equivalentGender', t.union(t.literal('male'), t.literal('female'))),
    t.property('riders', t.array(comprehensivePricingRiderSchema)),
    t.property('parties', t.array(comprehensivePricingPartySchema)),
    t.optionalProperty('fees', t.intersection(comprehensivePricingCostsSchema, feesInformationSchema)),
  ),
);

export type ComprehensivePricingContract = t.Infer<typeof comprehensivePricingContractSchema>;

export const comprehensivePricingSchema = t.intersection(
  comprehensivePricingCostsSchema,
  t.object(
    t.property('supportedPaymentModes', t.array(t.tsEnum(ComprehensivePricingSupportedPaymentModes))),
    t.property('contracts', t.array(comprehensivePricingContractSchema)),
  ),
);

export type ComprehensivePricing = t.Infer<typeof comprehensivePricingSchema>;

const nodeIdsToUpdateSchema = t.array(
  t.object(
    t.property('nodeId', t.string),
    t.property('value', t.union(t.string, t.number)),
    t.property('insuredIndex', t.number),
  ),
);

export type ComprehensivePricingNodeIdsToUpdate = t.Infer<typeof nodeIdsToUpdateSchema>;

const comprehensivePricingAndNodeIdsToUpdateSchema = t.object(
  t.property('pricing', comprehensivePricingSchema),
  t.optionalProperty('nodeIdsToUpdate', nodeIdsToUpdateSchema),
);

export type ComprehensivePricingAndNodeIdsToUpdate = t.Infer<typeof comprehensivePricingAndNodeIdsToUpdateSchema>;

export enum ComprehensivePricingAPIReturnValueErrors {
  missingAnswers = 'missingAnswers',
  missingOwner = 'missingOwner',
}

const comprehensivePricingAPIReturnValueSchema = t.discriminatedUnion(
  '_tag',
  t.intersection(comprehensivePricingAndNodeIdsToUpdateSchema, t.object(t.property('_tag', t.literal('success')))),
  t.object(
    t.property('_tag', t.literal('error')),
    t.property('error', t.tsEnum(ComprehensivePricingAPIReturnValueErrors)),
  ),
);

export type ComprehensivePricingAPIReturnValue = t.Infer<typeof comprehensivePricingAPIReturnValueSchema>;
